import { BadgeCheck, MapPin, Clock, Package, Gauge, Globe2, Award, ArrowUpRight, Building2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { SUPPLIERS } from "@/data/content";

const MetaRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="flex items-center gap-2 text-xs text-slate-500"><Icon className="w-3.5 h-3.5 text-slate-400" />{label}</span>
    <span className="text-xs font-semibold text-slate-800">{value}</span>
  </div>
);

export const SupplierProfiles = () => {
  return (
    <section id="suppliers" data-testid="suppliers-section" className="relative py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>Supplier Profiles</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold font-heading tracking-tight leading-tight text-slate-900">
              Clean, complete, factory-direct profiles.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Everything you need to shortlist a manufacturer — capabilities, certifications and verified data — in one premium view.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SUPPLIERS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <div data-testid={`supplier-card-${i}`} className="group h-full flex flex-col bg-white border border-slate-200/80 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-[0_24px_50px_rgba(15,23,42,0.1)] hover:-translate-y-1.5 hover:border-blue-200">
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image} alt={`${s.name} factory`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-emerald-700 text-xs font-semibold border border-emerald-100 shadow-sm"><BadgeCheck className="w-3.5 h-3.5 text-emerald-500" />Verified</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-900 text-white text-sm font-semibold shrink-0">{s.initials}</span>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-slate-900 truncate">{s.name}</h3>
                      <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5"><Building2 className="w-3 h-3" />{s.industry}</p>
                    </div>
                  </div>
                  <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-3"><MapPin className="w-3.5 h-3.5 text-slate-400" />{s.location}<span className="text-slate-300">·</span>{s.years} years in business</p>

                  <div className="mt-4">
                    <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-2">Export Markets</div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.markets.map((m) => (<span key={m} className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100"><Globe2 className="w-3 h-3" />{m}</span>))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-2">Certifications</div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.certifications.map((c) => (<span key={c} className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200"><Award className="w-3 h-3" />{c}</span>))}
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/50 px-4 py-2">
                    <MetaRow icon={Clock} label="Response Time" value={s.responseTime} />
                    <MetaRow icon={Package} label="MOQ" value={s.moq} />
                    <MetaRow icon={Gauge} label="Factory Capacity" value={s.capacity} />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s.categories.map((cat) => (<span key={cat} className="text-[11px] text-slate-500 px-2 py-0.5 rounded-md bg-slate-100/70">{cat}</span>))}
                  </div>

                  <a href="#final-cta" data-testid={`supplier-view-${i}`} className="mt-6 inline-flex items-center justify-center gap-2 w-full rounded-full border border-slate-200 py-3 text-sm font-medium text-slate-800 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all duration-300 group/btn">
                    View Profile
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SupplierProfiles;
